import { motion } from "motion/react";
import type { MouseEvent, ReactNode } from "react";
import { cardHover, fadeUpItem, sectionReveal } from "./homepageMotion";

type MotionSectionProps = {
  id?: string;
  className?: string;
  children: ReactNode;
  viewportAmount?: number;
};

type GlassPanelProps = {
  className?: string;
  innerClassName?: string;
  children: ReactNode;
  tone?: "light" | "dark";
  hoverGlow?: boolean;
};

type SweepButtonProps = {
  href: string;
  children: ReactNode;
  className?: string;
  tone?: "gold" | "navy" | "ghost";
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

/**
 * Animated section wrapper with a consistent reveal.
 * @param props Animated section props
 * @returns Motion section shell
 */
export const MotionSection = ({ id, className = "", children, viewportAmount = 0.25 }: MotionSectionProps) => {
  return (
    <motion.section
      id={id}
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: viewportAmount }}
      variants={sectionReveal}
    >
      {children}
    </motion.section>
  );
};

/**
 * Reusable structural panel with crisp borders and restrained shadows.
 * @param props Panel props
 * @returns Panel container
 */
export const GlassPanel = ({
  className = "",
  innerClassName = "",
  children,
  tone = "light",
  hoverGlow = false,
}: GlassPanelProps) => {
  const outerToneClassName =
    tone === "light" ? "border border-gray-200 bg-white" : "border border-white/10 bg-white/[0.02]";

  const innerToneClassName = tone === "light" ? "bg-white" : "bg-transparent";

  const hoverClassName = hoverGlow
    ? "transition-all duration-150 hover:border-(--brand-primary) hover:shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
    : "";

  const shadowClassName = tone === "light" ? "shadow-sm" : "shadow-none";

  return (
    <motion.div
      className={`rounded-xl ${shadowClassName} ${outerToneClassName} ${hoverClassName} ${className}`}
      whileHover={hoverGlow ? cardHover : undefined}
      whileTap={hoverGlow ? { scale: 0.99 } : undefined}
    >
      <div className={`rounded-[11px] ${innerToneClassName} ${innerClassName}`}>{children}</div>
    </motion.div>
  );
};

/**
 * CTA button with a sweeping shine animation.
 * @param props Button props
 * @returns Animated anchor button
 */
export const SweepButton = ({ href, children, className = "", tone = "gold", onClick }: SweepButtonProps) => {
  const toneClassName =
    tone === "gold"
      ? "border border-[#DDA74F] !bg-[#DDA74F] !text-[#131B2A] shadow-none hover:border-[#E9B969] hover:!bg-[#E9B969] hover:!text-[#131B2A]"
      : tone === "navy"
        ? "border border-[#131B2A] !bg-[#131B2A] !text-white shadow-none hover:border-[#0F1624] hover:!bg-[#0F1624] hover:!text-white"
        : "border border-gray-200 bg-white !text-[#131B2A] shadow-none hover:border-[#131B2A]/25 hover:bg-[#FBFBFB] hover:!text-[#131B2A]";

  return (
    <motion.a
      href={href}
      onClick={onClick}
      className={`inline-flex min-h-14 items-center justify-center rounded-lg px-7 text-sm font-semibold transition-colors duration-150 ${toneClassName} ${className}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUpItem}
      whileHover={{ scale: 0.98 }}
      whileTap={{ scale: 0.95 }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
    >
      <span className="inline-flex items-center gap-2">{children}</span>
    </motion.a>
  );
};
